import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaCheckCircle, FaExclamationCircle } from 'react-icons/fa';
import FloatingLabelSelect from '../ui/FloatingLabelSelect';
import { fetchClientData } from '../../services/adminApiService';
import { fetchRoleData } from '../../services/roleApiService';
import { fetchMenusByRoleId, updateRoleMenus } from '../../services/menuApiService';

const ClientRoleMenuPermission = () => {
  const [isEditing, setIsEditing] = useState(false);
    const [apiResponse, setApiResponse] = useState(null);
  const [clients, setClients] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);

  // Initialize formValues state to include clientId and menuPermissions
  const [formValues, setFormValues] = useState({
    clientId: '', 
    roleId: '', 
    menuPermissions: [] // Initially empty
  });

  const [errors, setErrors] = useState({});
  const [selectAllChecked, setSelectAllChecked] = useState(false); // State for "Check All" checkbox

    // Simple validation function
    const validate = () => {
        const newErrors = {};
        if (!formValues.clientId) newErrors.clientId = 'Organization is required';
        if (!formValues.roleId) newErrors.roleId = 'Role is required';
        return newErrors;
    };

    const handleBlur = (field) => {
        const validationErrors = validate();
        setErrors(validationErrors);
    };

    const getClients = async () => {
        try {
            const clientData = await fetchClientData();
            setClients(clientData.map(client => ({ value: client.clientId, label: client.clientName })));
        } catch (error) {
            console.error("Error fetching countries:", error);
        }
    };

    const inputClasses = (field) =>
        errors[field] ? 'border-red-500' : 'border-gray-300';


    useEffect(() => {
        getClients();
    }, []);

    const fetchMenusByRole = async (roleId) => {
        try {
            const roleMenuData = await fetchMenusByRoleId(roleId);
            const updatedMenus = roleMenuData.map(menu => ({
                roleId: roleId,
                menuItemId: menu.menuItemId,
                menuItemName: menu.menuItemName,
                activeStatus: menu.roleId === roleId ? 1 : 0 // Set activeStatus based on clientId match
              }));
              setFormValues({
                ...formValues,
                roleId: roleId,
                menuPermissions: updatedMenus
              });
        } catch (error) {
            console.error("Error fetching states:", error);
        }
    };

    const fetchRolesByClient = async (clientId) => {
        try {
            const roleData = await fetchRoleData(clientId);
            setRoles(roleData.map(role => ({ value: role.roleId, label: role.roleName })));
        } catch (error) {
            console.error("Error fetching states:", error);
        }
    };
  // Handle client selection
  const handleClientChange = (option) => {
    const selectedClientId = option.value;
    setFormValues({ ...formValues, clientId: option.value, roleId: '' }); 
    fetchRolesByClient(selectedClientId);
  };

   // Handle Role selection
   const handleRoleChange = (option) => {
    const selectedRoleId = option.value;
    fetchMenusByRole(selectedRoleId);
  };

  // Handle menu checkbox change
  const handleCheckboxChange = (menuItemId) => {
    const updatedMenuPermissions = formValues.menuPermissions.map(menu => 
      menu.menuItemId === menuItemId
        ? { ...menu, activeStatus: menu.activeStatus === 1 ? 0 : 1 } // Toggle activeStatus
        : menu
    );

    // Update formValues with the new menu permissions
    setFormValues(prevValues => ({
      ...prevValues,
      menuPermissions: updatedMenuPermissions
    }));
  };

  // Handle update button click
  const handleUpdate = async (e) => {
    e.preventDefault();
    if (formValues.roleId === null) {
      alert("Please select a Role before updating.");
      return;
    }

    const updatedMenus = formValues.menuPermissions.map(menu => ({
      roleId: formValues.roleId,
      menuItemId: menu.menuItemId,
      menuItemName: menu.menuItemName,
      activeStatus: menu.activeStatus // Keep the updated activeStatus
    }));

    //console.log("Updated menu permissions for Role:", formValues.roleId);
    //console.log("Selected Menus:", updatedMenus);

   
        const validationErrors = validate();
        if (Object.keys(validationErrors).length === 0) {
            try {
                //console.log('Form submitted', formValues);
                setLoading(true);
                let  data = await updateRoleMenus(updatedMenus);

                setApiResponse(data);
                if (!data.error) {
                    resetFormValues();
                    setIsEditing(false);
                    //setClients([]);
                } else {
                    return;
                }
            } catch (err) {
                const validationErrors = {};
                err.inner.forEach(error => {
                    validationErrors[error.path] = error.message;
                });
                setErrors(validationErrors);
                setApiResponse({ err: 'Failed to submit form. Please try again.' });
            } finally {
                setLoading(false);
            }
            setErrors({});
        } else {
            setErrors(validationErrors);
        }
    // Call API to update the permissions for the selected client
  };
  const resetFormValues = () => {
    setFormValues({
        clientId: '',
        roleId: '', 
    menuPermissions: []
    });
    setIsEditing(false);
    setErrors({});
    //setApiResponse(null);
}

const handleSelectAllChange = () => {
  const updatedMenuPermissions = formValues.menuPermissions.map(menu => ({
    ...menu,
    activeStatus: selectAllChecked ? 0 : 1 // Toggle based on "Check All" state
  }));
  setSelectAllChecked(!selectAllChecked); // Toggle "Check All" checkbox state
  setFormValues(prevValues => ({
    ...prevValues,
    menuPermissions: updatedMenuPermissions
  }));
};

// Check if all individual checkboxes are selected
const isAllChecked = formValues.menuPermissions.every(menu => menu.activeStatus === 1);

  return (
    <div className="container mx-auto p-6 bg-white rounded-lg border">
        <form onSubmit={handleUpdate} className="space-y-6 w-full">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                        {loading ? (
                            <p>Loading countries...</p>
                        ) : (
                            <FloatingLabelSelect
                                label="Select Organization"
                                id="clientId"
                                name="clientId"
                                value={clients.find(client => client.value === formValues.clientId) || null} // Handle no selection case with null
                                onChange={handleClientChange}//,{(option) => setFormValues({ ...formValues, clientId: option.value })}
                                onBlur={() => handleBlur('clientId')}
                                className={inputClasses('clientId')}
                                options={clients}
                                isSearchable={true}
                            />
                        )}
                        {errors.clientId && <div className="text-red-500 text-sm">{errors.clientId}</div>}
                    </div>

                    <div>
                                    <>
                                        {loading ? (
                                            <p>Loading states...</p>
                                        ) : (
                                            <FloatingLabelSelect
                                                label="Select Role"
                                                id="roleId"
                                                 name="roleId"
                                                value={roles.find(role => role.value === formValues.roleId) || null} // Use null if no state is selected
                                                onChange={handleRoleChange}
                                               
                                                onBlur={() => handleBlur('roleId')}
                                                className={inputClasses('roleId')}
                                                options={roles}
                                                isSearchable={true}
                                            />

                                        )}
                                    </>
                                    {errors.roleId && <div className="text-red-500 text-sm">{errors.roleId}</div>}
                                    </div>
                                
                </div>
      

     {/* Menu List (Display only if client is selected) */}
     {formValues.roleId && (
          <div>
            <h3 className="text-lg font-semibold mb-2">Menus List:</h3>
            <div className="space-y-2">
              {/* Check All Checkbox */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={isAllChecked}
                  onChange={handleSelectAllChange}
                  className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                />
                <label className="ml-2 text-gray-700 cursor-pointer">Check All</label>
              </div>
              <hr/>
              {formValues.menuPermissions.map(menu => (
                <div key={menu.menuItemId} className="flex items-center">
                  <input
                    type="checkbox"
                    checked={menu.activeStatus === 1}
                    onChange={() => handleCheckboxChange(menu.menuItemId)}
                    className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                  />
                  <label className="ml-2 text-gray-700 cursor-pointer" onClick={() => handleCheckboxChange(menu.menuItemId)}>
                    {` ${menu.menuItemName}`} {/* You can replace this with menu.label if available */}
                  </label>
                </div>
              ))}
            </div>

            {/* Update Button */}
            <button type="button" onClick={handleUpdate} className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600">
              Update
            </button>
          </div>
        )}
      </form>
      {apiResponse && (
                    <motion.div
                        className={`mt-4 flex items-center ${apiResponse.error ? 'text-red-500' : 'text-green-500'}`}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                    >
                        {apiResponse.error ? (
                            <>
                                <FaExclamationCircle className="mr-2" />
                                {apiResponse.error}
                            </>
                        ) : (
                            <>
                                <FaCheckCircle className="mr-2" />
                                {apiResponse.success}
                            </>
                        )}
                    </motion.div>
                )}
    </div>
  );
};


const TestPage = () => {
    return (<div  className="container mx-auto p-6">
            <div><h3>Role Menu Permissions</h3></div>
            {/* <MenuPermission data={data} clientData={clients} /> */}
            <ClientRoleMenuPermission  />
            </div>)
};

export default TestPage;
